import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useImportDataOptions } from "@/hooks/use-import-data.hook.ts";
import { ImportDataUpdateBehaviorField } from "@/pages/settings/import-data/edit/components/ImportDataUpdateBehaviorField.tsx";
import {
  ColumnFormData,
  MatchFormData,
} from "@/pages/settings/import-data/edit/import-data-edit.types.ts";
import { getEmployeeCompensationPerTypes } from "@/types/employees/employee-compensation.types.ts";
import { ApiFieldValueTypes } from "@/types/custom-field.types.ts";
import {
  ApiImportDataType,
  ApiImportGlobalUpdateBehavior,
  ApiImportNewValueBehavior,
  ApiImportParseColumn,
} from "@/types/import-data.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  Select,
  SelectItem,
  SelectValueType,
  Box,
  Button,
  Stack,
  Switch,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Avatar,
  Radio,
  Tag,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Control, Controller, FieldErrors, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

const allowedFieldTypes = [
  ApiFieldValueTypes.text,
  ApiFieldValueTypes.textarea,
  ApiFieldValueTypes.email,
  ApiFieldValueTypes.phone,
  ApiFieldValueTypes.url,
  ApiFieldValueTypes.number,
  ApiFieldValueTypes.percentage,
  ApiFieldValueTypes.date,
  ApiFieldValueTypes.switch,
  ApiFieldValueTypes.select,
];

const VALUES_PREVIEW_LIMIT = 5;

interface ImportDataValuesListProps {
  values: string[];
  isTruncated?: boolean;
}

const ImportDataValuesList = ({ values, isTruncated = false }: ImportDataValuesListProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleValues = isExpanded ? values : values.slice(0, VALUES_PREVIEW_LIMIT);
  const hiddenValuesCount = values.length - VALUES_PREVIEW_LIMIT;

  if (!values.length) return null;

  return (
    <Stack gap={"sm"}>
      <Stack
        direction={"horizontal"}
        gap={"xs"}
        className={twMerge("flex-wrap", isExpanded ? "max-h-40 overflow-y-auto" : undefined)}
      >
        {visibleValues.map((value) => (
          <Tag key={value}>{value}</Tag>
        ))}
        {isTruncated && <Text className={"text-secondary"}>{t("import_data:and_others")}</Text>}
      </Stack>
      {hiddenValuesCount > 0 && (
        <Box>
          <Button variant={"secondary"} onClick={() => setIsExpanded((value) => !value)}>
            {isExpanded
              ? t("common:show_less")
              : t("common:show_more_template", { count: hiddenValuesCount })}
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export interface ImportDataMatchColumnProps {
  index: number;
  column: ApiImportParseColumn;
  control: Control<MatchFormData>;
  isEnabled: boolean;
  onToggle: () => void;
  formColumn?: ColumnFormData;
  errors: FieldErrors<MatchFormData>;
  asFieldOptions: SelectItem[];
  onAsFieldChange: (newValue: string) => void;
  addedFieldsMap: Record<string, { id: string; title: string }>;
  setAddedFieldsMap: React.Dispatch<
    React.SetStateAction<Record<string, { id: string; title: string }>>
  >;
  addNewFieldToCache: (
    fieldKey: string,
    title: string,
    fieldImportType: ApiImportDataType,
    tableId?: number | null
  ) => void;
  setValue: UseFormSetValue<MatchFormData>;
  selectedImportBehavior: ApiImportGlobalUpdateBehavior;
}

export function ImportDataMatchColumn({
  index,
  column,
  control,
  isEnabled,
  onToggle,
  formColumn,
  errors,
  asFieldOptions,
  onAsFieldChange,
  addedFieldsMap,
  setAddedFieldsMap,
  addNewFieldToCache,
  setValue,
  selectedImportBehavior,
}: ImportDataMatchColumnProps) {
  const { t } = useTranslation();
  const { getSlug, truncate } = useFormatter();
  const { getUpdateBehaviorOptions, getNewValuesBehaviorOptions } = useImportDataOptions();
  const { fieldTypeInfo } = useCustomFieldsHook();
  const updateBehaviorOptions = getUpdateBehaviorOptions();
  const newValuesBehaviorOptions = getNewValuesBehaviorOptions();
  const fieldTypeOptions = useMemo(
    () =>
      Object.entries(fieldTypeInfo)
        .filter(([key]) => allowedFieldTypes.includes(key as ApiFieldValueTypes))
        .map(([key, info]) => ({ id: key, title: info.title })),
    [fieldTypeInfo]
  );
  const fieldError = errors?.columns?.[index]?.as_field?.message;
  const createNewValuesError = errors?.columns?.[index]?.create_new_values?.message;

  return (
    <Stack className={`card-panel`} gap={"2xl"}>
      <Stack gap={"2xl"}>
        <Stack direction={"horizontal"} gap={"sm"} justify={"between"}>
          <Box className={twMerge("break-word", isEnabled ? "" : "opacity-50")}>
            <Title
              header={column.header}
              caption={t("import_data:samples", {
                list: column.sample_values.map((v) => truncate(v, 80)).join(", "),
              })}
            />
          </Box>

          <Tooltip>
            <TooltipTrigger>
              <Switch value={String(index)} onChange={onToggle} checked={isEnabled} />
            </TooltipTrigger>
            <TooltipContent>
              {t(isEnabled ? "import_data:exclude_field" : "import_data:include_field")}
            </TooltipContent>
          </Tooltip>
        </Stack>

        {isEnabled && (
          <Stack gap={"2xl"}>
            <Stack direction={"horizontal"} gap={"sm"} items={"center"} className={"flex-wrap"}>
              <Text>{t("import_data:as_field")}</Text>
              <Controller
                control={control}
                name={`columns.${index}.as_field`}
                render={({ field: { value, onChange } }) => {
                  const columnImportType = formColumn?.import_type;
                  const tableId = formColumn?.table_id;

                  const handleSelectChange = (newValue: SelectValueType) => {
                    onChange(newValue ?? null);
                    onAsFieldChange(`${newValue}`);
                  };

                  const handleAddNew = (text: string) => {
                    if (!text.length) return;

                    const slug = getSlug(text);
                    setAddedFieldsMap((prev) => ({
                      ...prev,
                      [slug]: { id: slug, title: text },
                    }));
                    addNewFieldToCache(slug, text, columnImportType as ApiImportDataType, tableId);
                    setTimeout(() => onChange(slug), 0);
                    setValue(`columns.${index}.new_custom_field_name`, text);
                    const firstFieldType = fieldTypeOptions[0]?.id as ApiFieldValueTypes;
                    if (firstFieldType) {
                      setValue(`columns.${index}.new_custom_field_type`, firstFieldType);
                    }
                  };

                  return (
                    <Stack
                      gap="xs"
                      direction={"horizontal"}
                      items={"center"}
                      data-error={fieldError ? "true" : undefined}
                      className={
                        !columnImportType ? "opacity-50 pointer-events-none touch-none" : ""
                      }
                    >
                      <Select
                        value={value}
                        onChange={handleSelectChange}
                        onAddNew={
                          columnImportType === ApiImportDataType.employee ? handleAddNew : undefined
                        }
                        addNewPrefix={t("common:add_field")}
                        options={asFieldOptions}
                        emptyTitle={t("common:choose_field")}
                        variant="button"
                        disabled={!columnImportType}
                        searchable
                        renderSelectedTitle={(selected: SelectItem[]) =>
                          selected
                            .map(
                              (item: SelectItem) =>
                                `${item.group ? `${item.group}: ` : ""} ${item.title}`
                            )
                            .join(", ")
                        }
                      />
                      {fieldError && <Text className="text-danger text-sm">{fieldError}</Text>}
                    </Stack>
                  );
                }}
              />
              {formColumn?.as_field && addedFieldsMap[formColumn.as_field] && (
                <Stack className={"flex-wrap"} direction={"horizontal"} gap={"sm"} items={"center"}>
                  <Text>{t("custom_fields:field_type").toLowerCase()}</Text>
                  <Controller
                    control={control}
                    name={`columns.${index}.new_custom_field_type`}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        value={value}
                        onChange={onChange}
                        options={fieldTypeOptions}
                        emptyTitle={t("common:select")}
                        variant="button"
                      />
                    )}
                  />
                </Stack>
              )}
              {(formColumn?.as_field === "salary_gross" ||
                formColumn?.as_field === "salary_net") && (
                <Stack className={"flex-wrap"} direction={"horizontal"} gap={"sm"} items={"center"}>
                  <Text>{t("common:period").toLowerCase()}</Text>
                  <Controller
                    control={control}
                    name={`columns.${index}.salary_period`}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        value={value}
                        onChange={onChange}
                        options={getEmployeeCompensationPerTypes()}
                        emptyTitle={t("common:select")}
                        variant="button"
                      />
                    )}
                  />
                </Stack>
              )}
              {selectedImportBehavior !== ApiImportGlobalUpdateBehavior.create_only && (
                <ImportDataUpdateBehaviorField
                  control={control}
                  name={`columns.${index}.update_behavior`}
                  options={updateBehaviorOptions}
                />
              )}
            </Stack>

            {formColumn?.as_field === "salary_currency" &&
              column.invalid_values &&
              column.invalid_values.count > 0 && (
                <Stack className={"bg-dark/5 border-1 border-dark/5 rounded-sm p-4"} gap={"lg"}>
                  <Avatar
                    size={"md"}
                    emoji={formColumn.fallback_currency ? ":white_check_mark:" : ":warning:"}
                  />
                  <Title
                    header={
                      formColumn.fallback_currency
                        ? t("import_data:unrecognized_currencies_substituted", {
                            currency: formColumn.fallback_currency,
                          })
                        : t("import_data:unrecognized_currencies")
                    }
                    size={"sm"}
                  />

                  <ImportDataValuesList
                    values={column.invalid_values.sample_values}
                    isTruncated={column.invalid_values.is_truncated}
                  />

                  <Stack>
                    <Title header={t("import_data:substitute_them_with")} size={"xs"} />
                    <Controller
                      control={control}
                      name={`columns.${index}.fallback_currency`}
                      render={({ field: { value, onChange } }) => (
                        <CurrencySelect
                          value={value}
                          onChange={onChange}
                          emptyTitle={t("common:select")}
                          searchable
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              )}
            {column.new_values && column.new_values.count > 0 && (
              <Stack className={"bg-dark/5 border-1 border-dark/5 rounded-sm p-4"} gap={"lg"}>
                <Avatar
                  size={"md"}
                  emoji={formColumn?.create_new_values ? ":white_check_mark:" : ":warning:"}
                />

                <Title
                  header={
                    formColumn?.create_new_values === ApiImportNewValueBehavior.create_new_values
                      ? t("import_data:new_values_will_be_created")
                      : formColumn?.create_new_values ===
                          ApiImportNewValueBehavior.skip_news_records
                        ? t("import_data:new_values_will_be_skipped")
                        : t("import_data:new_values_detected")
                  }
                  size={"sm"}
                />

                <ImportDataValuesList values={column.new_values.sample_values} />

                <Stack>
                  <Title header={t("import_data:choose_behaviour")} size={"xs"} paddingBottom />
                  <Controller
                    control={control}
                    name={`columns.${index}.create_new_values`}
                    render={({ field: { value, onChange } }) => (
                      <Stack>
                        {newValuesBehaviorOptions.map((option) => (
                          <Radio
                            key={String(option.id)}
                            name={`columns.${index}.create_new_values`}
                            value={option.id}
                            checked={value === option.id}
                            title={option.title}
                            onChange={() => onChange(option.id)}
                          />
                        ))}
                      </Stack>
                    )}
                  />
                  {createNewValuesError && (
                    <Text className={"text-danger text-sm"}>{createNewValuesError}</Text>
                  )}
                </Stack>
              </Stack>
            )}
            {column.invalid_values &&
              column.invalid_values.count > 0 &&
              formColumn?.as_field !== "salary_currency" && (
                <Stack className={"bg-dark/5 border-1 border-dark/5 rounded-sm p-4"} gap={"lg"}>
                  <Avatar size={"md"} emoji={":warning:"} />

                  <Title
                    header={t("import_data:invalid_values_for_field_type", {
                      field_type: t("common:date"),
                    })}
                    caption={t("import_data:invalid_values_will_be_skipped")}
                    size={"sm"}
                  />

                  <ImportDataValuesList
                    values={column.invalid_values.sample_values}
                    isTruncated={column.invalid_values.is_truncated}
                  />
                </Stack>
              )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
